import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pencil, Trash2 } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectPortalAbsences } from "../../features/portal/portal-slice";
import {
  createPortalAbsence,
  deletePortalAbsence,
  fetchPortalAbsences,
  updatePortalAbsence,
} from "../../features/portal/slice-extensions/portal-slice-extension";

interface PortalAbsencesCardProps {
  memberId: string;
}

const todayYmd = () => new Date().toISOString().slice(0, 10);

/** The member's own absences: list current/upcoming ones, record new, remove upcoming. */
const PortalAbsencesCard = ({ memberId }: PortalAbsencesCardProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const absences = useAppSelector(selectPortalAbsences(memberId));
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [note, setNote] = useState("");
  /** The absence id currently loaded into the form; null = recording a new one. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchPortalAbsences({ memberId }));
  }, [dispatch, memberId]);

  const dateFormat = new Intl.DateTimeFormat(i18n.language, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const formatRange = (from: string, to: string) => {
    const start = dateFormat.format(new Date(`${from}T00:00:00`));
    const end = dateFormat.format(new Date(`${to}T00:00:00`));
    return from === to ? start : `${start} – ${end}`;
  };

  const valid = startDate !== "" && endDate !== "" && startDate <= endDate;

  const resetForm = () => {
    setStartDate("");
    setEndDate("");
    setNote("");
    setEditingId(null);
  };

  const handleSubmit = async () => {
    setBusy(true);
    setError(null);
    const request = { startDate, endDate, note: note || null };
    const result = editingId
      ? await dispatch(updatePortalAbsence({ memberId, absenceId: editingId, request }))
      : await dispatch(createPortalAbsence({ memberId, request }));
    setBusy(false);
    if (createPortalAbsence.fulfilled.match(result) || updatePortalAbsence.fulfilled.match(result)) {
      resetForm();
    } else {
      setError(result.payload?.message ?? t("portal.absences.saveFailed", "Could not save the absence."));
    }
  };

  const startEditing = (absenceId: string, from: string, to: string, absenceNote: string | null) => {
    setEditingId(absenceId);
    setStartDate(from);
    setEndDate(to);
    setNote(absenceNote ?? "");
    setError(null);
  };

  const handleDelete = async (absenceId: string) => {
    setError(null);
    const result = await dispatch(deletePortalAbsence({ memberId, absenceId }));
    if (!deletePortalAbsence.fulfilled.match(result)) {
      setError(result.payload?.message ?? t("portal.absences.deleteFailed", "Could not remove the absence."));
    }
  };

  return (
    <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10">
      <h2 className="text-base font-semibold text-grey-20 dark:text-white">
        {t("portal.absences.title", "My absences")}
      </h2>
      <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
        {t("portal.absences.intro", "Let your instructor know when you are away.")}
      </p>

      {(absences ?? []).length === 0 ? (
        <p className="mt-3 text-sm text-grey-40 dark:text-grey-80">
          {t("portal.absences.empty", "No absences recorded.")}
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {(absences ?? []).map((absence) => (
            <li key={absence.id} className="flex items-center gap-2 text-sm">
              <div className="min-w-0 flex-1">
                <span className="text-grey-20 dark:text-white">
                  {formatRange(absence.startDate, absence.endDate)}
                </span>
                {absence.note && (
                  <span className="text-grey-40 dark:text-grey-80"> · {absence.note}</span>
                )}
              </div>
              {absence.endDate >= todayYmd() && (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() =>
                      startEditing(absence.id, absence.startDate, absence.endDate, absence.note ?? null)
                    }
                    aria-label={t("portal.absences.editAction", "Edit absence")}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleDelete(absence.id)}
                    aria-label={t("portal.absences.removeAction", "Remove absence")}
                  >
                    <Trash2 size={14} />
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 grid grid-cols-2 gap-2">
        <input
          type="date"
          className="input"
          value={startDate}
          min={todayYmd()}
          aria-label={t("portal.absences.fromLabel", "First day away")}
          onChange={(e) => setStartDate(e.target.value)}
          disabled={busy}
        />
        <input
          type="date"
          className="input"
          value={endDate}
          min={startDate || todayYmd()}
          aria-label={t("portal.absences.toLabel", "Last day away")}
          onChange={(e) => setEndDate(e.target.value)}
          disabled={busy}
        />
      </div>
      <input
        type="text"
        className="input mt-2"
        value={note}
        maxLength={500}
        placeholder={t("portal.absences.notePlaceholder", "Note (optional)")}
        onChange={(e) => setNote(e.target.value)}
        disabled={busy}
      />
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={busy || !valid}
        >
          {editingId
            ? t("portal.absences.saveAction", "Save changes")
            : t("portal.absences.addAction", "Record absence")}
        </button>
        {editingId && (
          <button type="button" className="btn btn-secondary" onClick={resetForm} disabled={busy}>
            {t("common.cancel", "Cancel")}
          </button>
        )}
      </div>

      {error && <p className="field-error mt-2">{error}</p>}
    </div>
  );
};

export default PortalAbsencesCard;
